import type { User } from '@hendrx/authentication/contracts/client';
import { HocuspocusProvider } from '@hocuspocus/provider';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import sample from 'lodash/sample';
import { createContext, ReactNode, useMemo } from 'react';
import { Doc as YDoc, UndoManager as YUndoManager } from 'yjs';
import { createStore, StoreApi } from 'zustand';

import { mantineDefaultColors } from 'shared/client/consts/mantineDefaultColors';
import { YjsDocumentType } from 'shared/types/YjsDocumentType';
import { getEnvVar } from 'shared/utils/getEnvVar';

import { useCurrentDocument } from './hooks/useCurrentDocument';
import { YjsDevTools } from '../YjsDevTools';

type ActiveUser = Pick<User, 'avatarUrl' | 'id' | 'firstName' | 'lastName' | 'role'> & {
  color: string;
  date: number;
};

type InitializeYjsParams = {
  documentName: string;
  documentType: YjsDocumentType;
  assignmentId?: string;
  user: User;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type YjsStoreType = {
  activeUsers: ActiveUser[];
  initializeYjs: (params: InitializeYjsParams) => () => void;
  /**
   * The collaboration server refused this document. Terminal: the provider disconnects on a refusal and does
   * not retry, so without this the screen waits on a sync that never comes.
   */
  isDenied: boolean;
  isSynced: boolean;
  undoManager: YUndoManager | null;
  yjsDocument: YDoc;
};

export const YjsStoreContext = createContext<StoreApi<YjsStoreType> | null>(null);

type YjsProviderProps = {
  children: ReactNode;
  document?: YDoc;
  onSynced?: (document: YDoc, documentType: string, params?: Record<string, unknown>) => void;
};

export function YjsProvider(props: YjsProviderProps) {
  const yjsStore = useMemo(
    () =>
      createStore<YjsStoreType>(set => {
        const initialState = {
          activeUsers: [],
          isDenied: false,
          isSynced: false
        };

        let document = props.document || new YDoc();
        const undoManager: YUndoManager | null = null;

        return {
          ...initialState,
          yjsDocument: document,
          undoManager,
          isSynced: false,
          initializeYjs: params => {
            const { documentName, documentType, assignmentId, user } = params;
            // undefined params are passed as `undefined` strings so we strip them beforehand
            const definedParams = omitBy(params, isUndefined);
            const provider = new HocuspocusProvider({
              document,
              name: `${documentType}/${documentName}/${assignmentId}`,
              parameters: definedParams,
              // Hocuspocus requires us to define it because we use onAuthenticate on the backend, but this token is not really being used.
              token: 'no_token',
              url: getEnvVar('VITE_WS_URL'),
              // The server answers a refusal — a draft owned by another organization, or no session reaching
              // the collaboration host — with a permission-denied message on the still-open socket, and this
              // provider then disconnects for good (Bug 535458).
              onAuthenticationFailed: () => {
                set(state => ({ ...state, isDenied: true }));
              },
              onAwarenessUpdate: ({ states: awarenessStates }) => {
                const userMap = new Map<string, ActiveUser>();

                awarenessStates.forEach(awarenessState => {
                  const user = awarenessState['user'];
                  const existingUser = userMap.get(user.id);
                  if (!existingUser || user.date < existingUser.date) {
                    userMap.set(user.id, user);
                  }
                });

                set(state => ({
                  ...state,
                  activeUsers: Array.from(userMap.values())
                }));
              },
              onSynced: () => {
                const rootMap = provider.document.getMap('root');
                props.onSynced?.(document, documentType, params);

                set(state => ({
                  ...state,
                  undoManager: new YUndoManager(rootMap),
                  isSynced: true
                }));
              }
            });

            provider.setAwarenessField('user', {
              avatarUrl: user.avatarUrl,
              color: sample(mantineDefaultColors),
              firstName: user.firstName,
              id: user.id,
              lastName: user.lastName,
              role: user.role,
              date: Date.now()
            } as ActiveUser);

            return () => {
              if (provider) {
                provider.destroy();
                document = new YDoc();

                set(() => ({
                  ...initialState,
                  yjsDocument: document,
                  undoManager: new YUndoManager(document.getMap('root'))
                }));
              }
            };
          }
        };
      }),
    [props.document]
  );

  return (
    <YjsStoreContext.Provider value={yjsStore}>
      {props.children}
      <DevTools />
    </YjsStoreContext.Provider>
  );
}

const DevTools = () => {
  const currentDocument = useCurrentDocument();
  return <YjsDevTools document={currentDocument} />;
};
